import { CommonModule } from '@angular/common';
import { Inject, NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule, Routes, Router } from '@angular/router';

import { InfoApiService, pdfPlaceholderPath, UserService } from '@atmi/core';
import { AuthComponent } from '@atmi/pages/auth/auth.component';
import { BaseManagerComponent } from '@atmi/pages/auth/base-manager/base-manager.component';
import { BaseComponent } from '@atmi/pages/auth/base/base.component';
import { CardDManagerComponent } from '@atmi/pages/auth/card-d-manager/card-d-manager.component';
import { CardFManagerComponent } from '@atmi/pages/auth/card-f-manager/card-f-manager.component';
import { CardMiManagerComponent } from '@atmi/pages/auth/card-mi-manager/card-mi-manager.component';
import { CardOAManagerComponent } from '@atmi/pages/auth/card-oa-manager/card-oa-manager.component';
import { CardSManagerComponent } from '@atmi/pages/auth/card-s-manager/card-s-manager.component';
import { ChildrenSCardsManagerComponent } from '@atmi/pages/auth/card-s-manager/children-s-cards-manager/children-s-cards-manager.component';
import { InfoCardSManagerComponent } from '@atmi/pages/auth/card-s-manager/info-card-s-manager/info-card-s-manager.component';
import { CardsComponent } from '@atmi/pages/auth/cards/cards.component';
import { HomeComponent } from '@atmi/pages/auth/home/home.component';
import { SourceArchivialManagerComponent } from '@atmi/pages/auth/source-archivial-manager/source-archivial-manager.component';
import { SourceBibliographicManagerComponent } from '@atmi/pages/auth/source-bibliographic-manager/source-bibliographic-manager.component';
import { SourcesArchivialComponent } from '@atmi/pages/auth/sources-archivial/sources-archivial.component';
import { SourcesBibliographicComponent } from '@atmi/pages/auth/sources-bibliographic/sources-bibliographic.component';
import { VocabulariesManagerComponent } from '@atmi/pages/auth/vocabularies-manager/vocabularies-manager.component';
import { SharedModule } from '@atmi/shared';
import { TranslateModule } from '@ngx-translate/core';

import { AuditModule, AuditsComponent } from '@sim/audit';
import { ReleaseNotesComponent } from '@sim/audit/components/release-notes/release-notes.component';

import { CollectionManagerComponent, CollectionModule, CollectionsComponent, } from '@sim/collections';
import { CollectionPrintComponent } from '@sim/collections/components/collection-print/collection-print.component';
import { FormModule } from '@sim/form';
import {
  MediaComponent,
  MediaConfigurationService,
  MediaManagerComponent,
  MediaManagerDataComponent,
  MediaModule,
  MediaModuleConfiguration,
} from '@sim/media';
import { PeopleComponent, PersonManagerComponent, PersonModule, } from '@sim/person';

import { RoleApiService, RoleManagerComponent, RoleModule, RolesComponent, RoleService, } from '@sim/role';
import { UserConfigurationService, UserManagerComponent, UserModuleConfiguration, UsersComponent, UsersModule, } from '@sim/users';

import { environment } from '@src/environments/environment';
import { UiModule } from '@ui/ui.module';
import { NgxSpinnerModule } from 'ngx-spinner';

import { map, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { Md5 } from 'ts-md5';


const components = [
  AuthComponent,
  BaseComponent,
  BaseManagerComponent,
  CardDManagerComponent,
  CardFManagerComponent,
  CardMiManagerComponent,
  CardOAManagerComponent,
  CardsComponent,
  CardSManagerComponent,
  InfoCardSManagerComponent,
  ChildrenSCardsManagerComponent,
  HomeComponent,
  SourceArchivialManagerComponent,
  SourceBibliographicManagerComponent,
  SourcesArchivialComponent,
  SourcesBibliographicComponent,
  VocabulariesManagerComponent,
];

const materials = [
  MatButtonModule,
  MatCardModule,
  MatChipsModule,
  MatExpansionModule,
  MatFormFieldModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatPaginatorModule,
  MatProgressSpinnerModule,
  MatSelectModule,
  MatSidenavModule,
  MatTabsModule,
];

const routes: Routes = [
  {
    children: [
      {
        children: [
          {
            component: HomeComponent,
            path: '',
          },
        ],
        path: 'home',
      },
      {
        loadChildren: () => import('../../features/profile/profile.module').then((m) => m.ProfileModule),
        path: 'profile',
      },
      {
        children: [
          {
            component: UsersComponent,
            path: '',
          },
          {
            component: UserManagerComponent,
            path: 'manager',
          },
          {
            component: UserManagerComponent,
            path: 'manager/:id',
          },
        ],
        path: 'users',
      },
      {
        children: [
          {
            component: RolesComponent,
            path: '',
          },
          {
            component: RoleManagerComponent,
            path: 'manager',
          },
          {
            component: RoleManagerComponent,
            path: 'manager/:id',
          },
        ],
        path: 'roles',
      },
      {
        children: [
          {
            component: MediaComponent,
            path: '',
          },
          {
            component: MediaManagerComponent,
            path: 'manager',
          },
          {
            component: MediaManagerDataComponent,
            path: 'manager/:id',
          },
        ],
        path: 'media',
      },
      {
        children: [
          {
            component: SourcesArchivialComponent,
            path: '',
          },
          {
            component: SourceArchivialManagerComponent,
            path: 'manager',
          },
          {
            component: SourceArchivialManagerComponent,
            path: 'manager/:id',
          },
        ],
        path: 'archival-sources',
      },
      {
        children: [
          {
            component: SourcesBibliographicComponent,
            path: '',
          },
          {
            component: SourceBibliographicManagerComponent,
            path: 'manager',
          },
          {
            component: SourceBibliographicManagerComponent,
            path: 'manager/:id',
          },
        ],
        path: 'bibs-sources',
      },
      {
        children: [
          {
            component: AuditsComponent,
            path: '',
          },
        ],
        path: 'audits',
      },
      {
        children: [
          {
            component: ReleaseNotesComponent,
            path: '',
          },
        ],
        path: 'release_notes',
      },
      {
        children: [
          {
            component: CardsComponent,
            path: '',
          },
          {
            component: CardOAManagerComponent,
            path: 'oa/manager',
          },
          {
            component: CardOAManagerComponent,
            path: 'oa/manager/:id',
          },
          {
            component: CardSManagerComponent,
            path: 's/manager',
          },
          {
            component: CardSManagerComponent,
            path: 's/manager/:id',
          },
          {
            component: CardFManagerComponent,
            path: 'f/manager',
          },
          {
            component: CardFManagerComponent,
            path: 'f/manager/:id',
          },
          {
            component: CardMiManagerComponent,
            path: 'mi/manager',
          },
          {
            component: CardMiManagerComponent,
            path: 'mi/manager/:id',
          },
          {
            component: CardDManagerComponent,
            path: 'd/manager',
          },
          {
            component: CardDManagerComponent,
            path: 'd/manager/:id',
          },
        ],
        path: 'cards',
      },
      {
        children: [
          {
            component: BaseComponent,
            path: '',
          },
          {
            component: BaseManagerComponent,
            path: 'manager',
          },
          {
            component: BaseManagerComponent,
            path: 'manager/:id',
          },
        ],
        path: 'base',
      },
      {
        children: [
          {
            component: VocabulariesManagerComponent,
            path: '',
          },
        ],
        path: 'vocabularies',
      },
      {
        children: [
          {
            component: CollectionsComponent,
            path: '',
          },
          {
            component: CollectionManagerComponent,
            path: 'manager',
          },
          {
            component: CollectionManagerComponent,
            path: 'manager/:id',
          },
          {
            component: CollectionPrintComponent,
            path: 'collection-print'
          },
        ],
        path: 'collections',
      },
      {
        children: [
          {
            component: PeopleComponent,
            path: '',
          },
          {
            component: PersonManagerComponent,
            path: 'manager',
          },
          {
            component: PersonManagerComponent,
            path: 'manager/:id',
          },
        ],
        path: 'people',
      }
    ],
    component: AuthComponent,
    path: '',
  },
];

@NgModule({
  declarations: [...components],
  exports: [
    SourceArchivialManagerComponent
  ],
  imports: [
    CommonModule,
    RouterModule.forChild(routes),
    FormModule,
    ...materials,
    SharedModule,
    TranslateModule,
    UiModule,
    NgxSpinnerModule,
    MediaModule,
    RoleModule,
    UsersModule,
    AuditModule.forRoot({ basePath: environment.basePath }),
    CollectionModule.forRoot({ basePath: environment.basePath }),
    PersonModule.forRoot({ basePath: environment.basePath }),
    MatCheckboxModule,
    MatTooltipModule,
  ]
})
export class AuthModule {
  constructor(
    private readonly roleService: RoleService,
    private readonly roleApi: RoleApiService,
    private readonly userApi: UserService,
    private readonly infoApi: InfoApiService,
    private readonly router: Router,
    @Inject(UserConfigurationService) private readonly userConfig: UserModuleConfiguration,
    @Inject(MediaConfigurationService) private readonly mediaConfig: MediaModuleConfiguration,
    @Inject(pdfPlaceholderPath) private readonly pdfPlaceholderUrl: string,
  ) {
    this.userConfig.roles = () => this.roleApi.index().pipe(map(response => response?.data as any))

    this.mediaConfig.binaryKeyGenerator = (() => {
      const token = this.userApi.selectStateSnasphot().token;
      const info = this.infoApi.selectStateSnapshot().info;
      const ipAddress = info?.ip_address;

      const key = `${token}${ipAddress}`;
      return Md5.hashStr(key);
    });

    this.mediaConfig.pdfPlaceholderPath = this.pdfPlaceholderUrl;

    // Verify stored user on server after module initialization
    setTimeout(async () => {
      try {
        await this.userApi.verifyStoredUser();
      } catch (error) {
        console.error('AuthModule: Error during user verification:', error);
      }
    }, 100);

    this.userApi.selectState()
      .pipe(
        map((state) => { console.log({ state }); return state.user; }),
        catchError((error) => {
          console.error('AuthModule: Error in user state subscription:', error);
          // Set role to undefined on error to ensure consistent state
          //this.roleService.setRoleById(undefined);
          return EMPTY;
        })
      )
      .subscribe(async (user) => {
        try {
          if (user?.role) {
            console.log('AuthModule: Setting role by ID:', user.role);
            await this.roleService.setRoleById(user.role);
            console.log('AuthModule: Role set successfully');
          } else {
            console.warn('AuthModule: User has no role ID, setting undefined role');
            await this.roleService.setRoleById(undefined);
          }
        } catch (error) {
          console.error('AuthModule: Error setting role by ID:', error);
          // Ensure role is set to undefined on error to unblock resolver
          try {
            await this.roleService.setRoleById(undefined);
          } catch (setError) {
            console.error('AuthModule: Failed to set undefined role:', setError);
          }

          // Check if error is 401 or 404 and redirect to login
          if (error && (error.status === 401 || error.status === 404)) {
            console.log('AuthModule: Authentication error detected, redirecting to login');
            this.router.navigate(['/login']);
          }
        }
      });
  }
}
